import type { PairedSalesAnalysisFeature, StudiesPair } from '@aloft/toolkit-shared';
import { AloftPrice } from '@aloft/toolkit-shared';
import { Block, Flex, Icon, Typography } from '@aloft/toolkit-sui';

import { useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';

import { Table, TableContainer, Tbody, Th, Thead, Tr } from '../../../../components/table.tsx';
import { useZodFormContext } from '../../../../hooks/useZodForm.ts';
import { createStudyResultString } from '../../../studies/utils/createStudyResultString.ts';
import { getIcon } from '../../../studies/utils/getIcon.ts';
import { makeAdjustResult } from '../../../studies/utils/makeAdjustResult.ts';
import { ADJUST_PAIRS_GRID_DEF } from '../../constants.ts';
import { schPair } from '../../schemas.ts';
import type { AdjustPairsGridDefConfig, PairedSalesFormSchema } from '../../types.ts';
import { createPairAdjustmentFormulaString } from '../../utils/createPairAdjustmentFormulaString.ts';
import { isPairedSalesCustomAnalysisFeature } from '../../utils/isPairedSalesCustomAnalysisFeature.ts';
import { AdjustPairsRow } from './AdjustPairsRow.tsx';
import { PairGridHeader } from './PairGridHeader.tsx';

type AdjustPairsProps = {
  pairIndex: number;
  term: PairedSalesAnalysisFeature;
  onSwapProperties: () => void;
};

export const AdjustPairs = ({ pairIndex, term, onSwapProperties }: AdjustPairsProps) => {
  const { control, setValue } = useZodFormContext<PairedSalesFormSchema>();

  // Necessary to force re-render on pair swap
  const swapKey = useWatch({
    name: `selectedPairs.${pairIndex}.p1.toolkitRecordID`,
    control,
  });

  const pair = useWatch({
    name: `selectedPairs.${pairIndex}`,
    control,
  });

  const safelyParsedPair = schPair.safeParse(pair);

  const parsedPairRef = useRef<StudiesPair>({ p1: {}, p2: {}, adjustments: {} });

  const parsedPair = safelyParsedPair.success ? safelyParsedPair.data : parsedPairRef.current;

  // Hold on to last set of valid values if parsing error occurs
  useEffect(() => {
    if (safelyParsedPair.success) {
      parsedPairRef.current = safelyParsedPair.data;
    }
  }, [safelyParsedPair]);

  const adjustResult = makeAdjustResult(parsedPair, AloftPrice.zero, term);

  return (
    <Flex
      height="100%"
      width="100%"
      overflow="auto"
      backgroundColor="surface6"
      paddingX={6}
      flexDirection="column"
      gap={3}
    >
      <Block paddingTop={3}>
        <Typography variant="h1">Adjust Pairs</Typography>
      </Block>

      <Flex width="100%" gap={4} flexGrow={1}>
        <TableContainer width="100%">
          <Table layout="fixed" width="650px">
            <Thead>
              <Tr>
                <Th colSpan={4} backgroundColor="white" height="48px">
                  <Flex
                    flexDirection="column"
                    justifyContent="center"
                    height="100%"
                    width="100%"
                    padding={2.5}
                    gap="4px"
                  >
                    <Flex alignItems="center" gap="0.5rem">
                      <Icon icon={getIcon(term)} size="lg" />
                      <Typography variant="h2" casing="capitalize">
                        {createStudyResultString(term, adjustResult.value)}
                      </Typography>
                    </Flex>
                    <Typography variant="body2">
                      {createPairAdjustmentFormulaString(term, parsedPair)}
                    </Typography>
                  </Flex>
                </Th>
              </Tr>
              <Tr>
                {(['p1', 'p2'] as const).map((p) => (
                  <PairGridHeader
                    key={p}
                    pairIndex={pairIndex}
                    property={p}
                    onSwapProperties={onSwapProperties}
                  />
                ))}
              </Tr>
            </Thead>

            <Tbody>
              {getGridDef(term).map((row) => {
                const key = `${row.propertyKey}_${swapKey}`;
                if (row.inputType === 'text' && row.labelKey !== undefined) {
                  const { labelKey } = row;

                  return (
                    <AdjustPairsRow
                      key={key}
                      pairIndex={pairIndex}
                      {...row}
                      label={parsedPair[row.labelKey] ?? row.label}
                      setLabel={(newLabel) =>
                        setValue(`selectedPairs.${pairIndex}.${labelKey}`, newLabel)
                      }
                    />
                  );
                }

                return <AdjustPairsRow key={key} pairIndex={pairIndex} {...row} />;
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Flex>
    </Flex>
  );
};

const getGridDef = (term: PairedSalesAnalysisFeature): AdjustPairsGridDefConfig[] => {
  const defaultDef = [...ADJUST_PAIRS_GRID_DEF];

  switch (term) {
    case 'cooling':
      defaultDef.push({
        propertyKey: 'cooling',
        adjustKey: 'cooling',
        label: 'Cooling',
        inputType: 'text',
      });

      break;
    case 'greenEnergyEfficient':
      defaultDef.push({
        propertyKey: 'greenEnergyEfficient',
        adjustKey: 'greenEnergyEfficient',
        label: 'Energy Efficiency',
        inputType: 'text',
      });

      break;
    case 'heating':
      defaultDef.push({
        propertyKey: 'heating',
        adjustKey: 'heating',
        label: 'Heating',
        inputType: 'text',
      });

      break;
    case 'pool':
      defaultDef.push({
        propertyKey: 'poolFeatures',
        adjustKey: 'pool',
        label: 'Pool',
        inputType: 'text',
      });

      break;
    default:
      if (isPairedSalesCustomAnalysisFeature(term)) {
        defaultDef.push({
          propertyKey: 'custom',
          adjustKey: 'custom',
          label: term.name,
          inputType: 'text',
        });
      }
  }

  return defaultDef;
};
